import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, isStaffRole, termLabel, type Course, type DegreeMarks } from "../api.js";
import { useAuth } from "../auth.js";

/**
 * Every course a teacher runs, in one table.
 *
 * The dashboard shows one course at a time, which answers "what is in this
 * course" but never "what am I running this semester, and which of these is
 * still empty". This is that view: what each offering contains, and the two
 * ways to retire one -- archive, which keeps the records, and delete, which is
 * only for an offering created by mistake.
 */
export default function Courses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user ? isStaffRole(user.role) : false;

  const [courses, setCourses] = useState<Course[]>([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = (includeArchived = showArchived) =>
    api
      .get<{ courses: Course[]; archivedCount: number }>(
        `/api/courses${includeArchived ? "?includeArchived=true" : ""}`
      )
      .then((r) => {
        setCourses(r.courses);
        setArchivedCount(r.archivedCount);
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    void load(showArchived);
  }, [showArchived]);

  // A student sees the same list without any of the actions -- see StudentCourses
  // below for why they get a page here at all.
  if (user && !isStaff) {
    return (
      <StudentCourses
        archivedCount={archivedCount}
        showArchived={showArchived}
        onShowArchived={setShowArchived}
        error={error}
      />
    );
  }

  /**
   * Deleting an offering is for one created by mistake. The server refuses as
   * soon as it holds assignments, exams or anyone but its teacher, and says
   * which -- so this only has to stop the accidental click and relay the answer.
   */
  async function deleteCourse(c: Course) {
    if (
      !confirm(
        `Delete ${c.code} — ${c.name} (${termLabel(c)})?\n\n` +
          `This cannot be undone. It only works while the offering is empty; ` +
          `if it has assignments, exams or students, archive it instead.`
      )
    )
      return;
    setError(null);
    setStatus(null);
    setBusy(c.id);
    try {
      await api.del(`/api/courses/${c.id}`);
      setStatus(`Deleted ${c.code} (${termLabel(c)}).`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the course.");
    } finally {
      setBusy(null);
    }
  }

  async function toggleArchive(c: Course) {
    const archiving = !c.archived;
    if (
      archiving &&
      !confirm(
        `Archive ${c.code} (${termLabel(c)})?\n\n` +
          `It disappears from course lists and stops accepting new submissions. ` +
          `All assignments, grades and shared questions are kept, and you can restore it later.`
      )
    )
      return;
    setError(null);
    setStatus(null);
    setBusy(c.id);
    try {
      await api.patch(`/api/courses/${c.id}`, { archived: archiving });
      setStatus(archiving ? `${c.code} archived.` : `${c.code} restored.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not change the course.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      {error && <div className="error-banner">{error}</div>}
      {status && <div className="notice">{status}</div>}

      {/* Its own header rather than the course tab strip: this page is a level
          above a course, and a strip whose other tabs need a course id would be
          half dead here. */}
      <div className="between" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Courses</h1>
          <div className="muted">Every course you teach, and what is in it</div>
        </div>
        <div className="row">
          <Link to="/library"><button>Question library</button></Link>
          <button className="primary" onClick={() => navigate("/?new=course")}>
            New course
          </button>
        </div>
      </div>

      <div className="card">
        <div className="between" style={{ marginBottom: 12 }}>
          {/* The page is already titled; this only has to say how many. */}
          <span className="muted" style={{ fontSize: 13 }}>
            {courses.length} course{courses.length === 1 ? "" : "s"}
          </span>
          {archivedCount > 0 && (
            <label className="row" style={{ marginBottom: 0, width: "auto" }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              <span className="muted" style={{ marginBottom: 0, fontSize: 13 }}>
                Show archived ({archivedCount})
              </span>
            </label>
          )}
        </div>

        {courses.length === 0 ? (
          <div className="muted">
            You have no courses yet. Use New course to create one — you will be enrolled as its
            teacher.
          </div>
        ) : (
          // Nothing in the row wraps, so on a narrow window the table scrolls
          // rather than squeezing the actions out of reach.
          <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Course</th>
                <th className="nowrap">Term</th>
                <th className="num">Students</th>
                <th className="num">Assignments</th>
                <th className="num">Exams</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => {
                // Deleting is only ever going to succeed on an empty offering,
                // so an occupied one offers archiving instead of a button whose
                // only outcome is an error.
                const empty =
                  c.assignmentCount === 0 && c.examCount === 0 && c.studentCount === 0;
                return (
                  <tr key={c.id}>
                    {/* Code and name are one thing -- which course this is --
                        so they share a cell and the row stays one line tall. */}
                    <td>
                      <div className="cell-title">
                        <Link to={`/assignments?course=${c.id}`}>{c.code}</Link>
                        {c.archived && (
                          <span className="badge draft" style={{ marginLeft: 6 }}>archived</span>
                        )}
                      </div>
                      <div className="cell-sub">{c.name}</div>
                    </td>
                    <td className="muted nowrap">{termLabel(c)}</td>
                    {/* An empty count is a dash: a column of zeroes reads as
                        data when it means "nothing here yet". */}
                    <Count n={c.studentCount} />
                    <Count n={c.assignmentCount} />
                    <Count n={c.examCount} />
                    <td className="actions">
                      <div className="row">
                        <Link to={`/assignments?course=${c.id}`}><button>Open</button></Link>
                        <Link to={`/courses/${c.id}/roster`}><button>Roster</button></Link>
                        <button disabled={busy === c.id} onClick={() => toggleArchive(c)}>
                          {c.archived ? "Restore" : "Archive"}
                        </button>
                        {empty && (
                          <button
                            className="danger"
                            disabled={busy === c.id}
                            onClick={() => deleteCourse(c)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        <p className="muted" style={{ fontSize: 12.5, marginBottom: 0, marginTop: 14 }}>
          A course with any work or anyone enrolled in it can only be archived — grades,
          submissions and shared questions all survive, and you can restore it. Delete only
          appears on an offering that is still empty.
        </p>
      </div>
    </div>
  );
}

function Count({ n }: { n: number }) {
  return <td className={n === 0 ? "num muted" : "num"}>{n === 0 ? "—" : n}</td>;
}

/**
 * Every course a student has taken, grouped by term.
 *
 * A degree is six or eight semesters of several subjects each, and the only
 * navigation a student had was one dropdown on the dashboard — which is fine at
 * four courses and unusable at thirty. Worse, courses are archived at the end of
 * a term, and that used to remove them from a student's view entirely: passing a
 * semester took away their own submissions, marks and code.
 *
 * So this is the degree-shaped view. It is deliberately read-only: every action
 * on the teacher's version of this page — archive, delete, roster — is a
 * teacher's, and a student's business here is finding a course and going to it.
 */
function StudentCourses({
  archivedCount,
  showArchived,
  onShowArchived,
  error,
}: {
  archivedCount: number;
  showArchived: boolean;
  onShowArchived: (v: boolean) => void;
  error: string | null;
}) {
  const [marks, setMarks] = useState<DegreeMarks | null>(null);
  const [marksError, setMarksError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<DegreeMarks>(`/api/me/marks${showArchived ? "?includeArchived=true" : ""}`)
      .then(setMarks)
      .catch((e) => setMarksError(e.message));
  }, [showArchived]);

  const courses = marks?.courses ?? [];

  // The API returns newest term first, so a degree reads backwards from now and
  // this only has to notice where one term ends.
  const terms: [string, typeof courses][] = [];
  for (const c of courses) {
    const key = termLabel(c);
    const last = terms[terms.length - 1];
    if (last && last[0] === key) last[1].push(c);
    else terms.push([key, [c]]);
  }

  const current = courses.filter((c) => !c.archived).length;
  const overall = marks?.overall;

  return (
    <div className="page">
      {(error || marksError) && <div className="error-banner">{error ?? marksError}</div>}

      <div className="between" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>My courses</h1>
          <div className="muted">
            {current} in progress
            {archivedCount > 0 && ` · ${archivedCount} finished`}
          </div>
        </div>
        {archivedCount > 0 && (
          <label className="row" style={{ marginBottom: 0, width: "auto" }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={showArchived}
              onChange={(e) => onShowArchived(e.target.checked)}
            />
            <span className="muted" style={{ marginBottom: 0, fontSize: 13 }}>
              Show past courses ({archivedCount})
            </span>
          </label>
        )}
      </div>

      {!marks ? (
        <div className="card muted">Loading…</div>
      ) : courses.length === 0 ? (
        <div className="card muted">
          You are not enrolled in any course yet. Your college's administrator adds students to a
          course from its roster.
        </div>
      ) : (
        <>
          {overall && overall.outOf > 0 && (
            <div className="card">
              <div style={{ fontSize: 28, fontWeight: 600 }}>
                {overall.earned}{" "}
                <span className="muted" style={{ fontSize: 18, fontWeight: 400 }}>
                  / {overall.outOf}
                </span>
                {overall.percent !== null && (
                  <span className="muted" style={{ fontSize: 18, fontWeight: 400 }}>
                    {" "}({overall.percent}%)
                  </span>
                )}
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                across {overall.assessedCourseCount} of {overall.courseCount} course
                {overall.courseCount === 1 ? "" : "s"}
                {overall.pendingCount > 0 && ` · ${overall.pendingCount} waiting on a mark`}
                {overall.outstandingCount > 0 && ` · ${overall.outstandingCount} not yet attempted`}
              </div>
              {/* Said plainly, because a single number across a degree is the
                  one most likely to be mistaken for a grade. It is not one: it
                  is the lab marks released so far, added up. */}
              <p className="muted" style={{ fontSize: 13, margin: "10px 0 0" }}>
                This is the lab work you have been marked on so far, added up. It is not a grade or
                a CGPA — how your college weighs one course against another, or labs against
                everything else, is their decision, not this page's.
              </p>
            </div>
          )}

          {/* One card per term rather than one long table: the term is the thing
              a student navigates by ("second year, first semester"), and a
              heading they can scan beats a column they have to read. */}
          {terms.map(([term, group]) => (
            <div className="card" key={term}>
              <div style={{ marginBottom: 10, color: "var(--muted)", fontSize: 12 }}>
                {term.toUpperCase()}
              </div>
              {group.map((c) => (
                <div className="test-row" key={c.id}>
                  <span className="name">
                    <Link to={`/assignments?course=${c.id}`}>{c.code}</Link>
                    {c.archived && (
                      <span className="badge draft" style={{ marginLeft: 6 }}>finished</span>
                    )}
                    <div className="cell-sub">{c.name}</div>
                  </span>
                  <span style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                    {/* A course with nothing marked yet gets a dash, not a zero:
                        "not assessed" and "scored nothing" are different facts. */}
                    {c.totals.assessedCount === 0 ? (
                      <span className="muted">not marked yet</span>
                    ) : (
                      <>
                        <strong>{c.totals.earned}</strong>
                        <span className="muted"> / {c.totals.outOf}</span>
                        {c.totals.percent !== null && (
                          <span className="muted"> · {c.totals.percent}%</span>
                        )}
                      </>
                    )}
                  </span>
                  <div className="row">
                    <Link to={`/courses/${c.id}/marks`}><button>My marks</button></Link>
                    <Link to={`/assignments?course=${c.id}`}><button>Open</button></Link>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      <p className="muted" style={{ fontSize: 12.5 }}>
        A finished course stays here for good. You can read everything you submitted and every
        mark you were given; you just cannot submit to it any more.
      </p>
    </div>
  );
}
